class CartDrawer extends HTMLElement {
	constructor() {
		super();
		this.handleHeaderCartClick = this.handleHeaderCartClick.bind(this);
		this.handleHeaderCartKeydown = this.handleHeaderCartKeydown.bind(this);
		this.setHeaderCartIconAccessibility =
			this.setHeaderCartIconAccessibility.bind(this);
		this.initializeNotificationRecommendations =
			this.initializeNotificationRecommendations.bind(this);
		this.pruneDrawerRecommendations =
			this.pruneDrawerRecommendations.bind(this);
		this.observeDrawerRecommendations =
			this.observeDrawerRecommendations.bind(this);
		this.disconnectDrawerObserver =
			this.disconnectDrawerObserver.bind(this);

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		document.addEventListener("click", this.handleHeaderCartClick);
		document.addEventListener("keydown", this.handleHeaderCartKeydown);
		window.addEventListener("pageshow", this.setHeaderCartIconAccessibility);
		this.setHeaderCartIconAccessibility();
		this.pruneDrawerRecommendations();
		this.observeDrawerRecommendations();
		this.initializeNotificationRecommendations();
	}

	disconnectedCallback() {
		document.removeEventListener("click", this.handleHeaderCartClick);
		document.removeEventListener("keydown", this.handleHeaderCartKeydown);
		window.removeEventListener(
			"pageshow",
			this.setHeaderCartIconAccessibility
		);
		this.disconnectDrawerObserver();
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		if (!cartLink) return;

		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");
	}

	getHeaderCartTrigger(target) {
		return typeof target?.closest === "function"
			? target.closest("#cart-icon-bubble")
			: null;
	}

	handleHeaderCartClick(event) {
		const cartLink = this.getHeaderCartTrigger(event.target);
		if (!cartLink) return;

		this.setHeaderCartIconAccessibility();
		event.preventDefault();
		this.open(cartLink);
	}

	handleHeaderCartKeydown(event) {
		if ((event.code || "").toUpperCase() !== "SPACE") return;

		const cartLink = this.getHeaderCartTrigger(event.target);
		if (!cartLink) return;

		event.preventDefault();
		this.open(cartLink);
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);

		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add("cart-overflow-hidden");

		if (!isSafari()) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.add("overflow-clip");
		}
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("cart-overflow-hidden");

		if (!isSafari()) {
			document.body.classList.remove("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-clip");
		}

		setTimeout(() => {
			let notification = this.querySelector(".notification");

			if (notification) {
				const activeItem = this.querySelector(
					`.cart-item[data-product-id="${this.productId}"`
				);
				notification.classList.remove("notification-open");
				activeItem?.classList.remove("active");
			}

			this.pruneDrawerRecommendations();
		}, 1000);
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			sectionElement.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
		});

		let notification = this.querySelector(".notification");

		if (notification) {
			const activeItem = this.querySelector(
				`.cart-item[data-product-id="${this.productId}"`
			);
			notification.classList.add("notification-open");
			activeItem?.classList.add("active");
		}

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay").addEventListener(
				"click",
				this.close.bind(this)
			);
			this.setHeaderCartIconAccessibility();
			this.pruneDrawerRecommendations();
			this.initializeNotificationRecommendations();
			this.open();
		});
	}

	pruneDrawerRecommendations() {
		const drawer = this.querySelector("#CartDrawer");
		if (!drawer) return;

		const selectors = [
			".product-recommendations__list",
			".cart-drawer__recommendations-head",
			".product-recommendations__slider-nav",
			".product-recommendations__product--drawer",
			'[class*="upsell"]',
			'[class*="Upsell"]',
			'[id*="upsell"]',
			'[id*="Upsell"]',
			'[class*="cross-sell"]',
			'[class*="cross_sell"]',
			'[class*="CrossSell"]',
			'[id*="cross-sell"]',
			'[id*="cross_sell"]',
		];

		drawer.querySelectorAll(selectors.join(",")).forEach((node) => {
			const recommendationRoot = node.closest(
				".product-recommendations__list, .cart-drawer__recommendations-head, .product-recommendations, [class*='upsell'], [class*='cross-sell'], [class*='cross_sell']"
			);
			(recommendationRoot || node).remove();
		});

		drawer.querySelectorAll("h1, h2, h3, h4, p, span, div, button").forEach((node) => {
			const text = (node.textContent || "").trim().toLowerCase();
			if (
				!text ||
				!(
					text.includes("completa tu look") ||
					text.includes("también te pueden gustar") ||
					text.includes("tambien te pueden gustar") ||
					text.includes("you may also like") ||
					text.includes("complete your look")
				)
			) {
				return;
			}

			const recommendationRoot = node.closest(
				".product-recommendations__list, .cart-drawer__recommendations-head, .product-recommendations, [class*='upsell'], [class*='cross-sell'], [class*='cross_sell']"
			);
			if (recommendationRoot) recommendationRoot.remove();
		});
	}

	observeDrawerRecommendations() {
		const drawer = this.querySelector("#CartDrawer");
		if (!drawer || this.drawerRecommendationObserver) return;

		this.drawerRecommendationObserver = new MutationObserver(() => {
			this.pruneDrawerRecommendations();
		});

		this.drawerRecommendationObserver.observe(drawer, {
			childList: true,
			subtree: true,
		});
	}

	disconnectDrawerObserver() {
		if (!this.drawerRecommendationObserver) return;
		this.drawerRecommendationObserver.disconnect();
		this.drawerRecommendationObserver = null;
	}

	initializeNotificationRecommendations() {
		if (typeof Swiper === "undefined") return;

		this.querySelectorAll(".js-recommendation-swiper").forEach((slider) => {
			if (slider.swiper) {
				slider.swiper.update();
				return;
			}

			const mobileLayout = Number(slider.dataset.mobileLayout || 1.08);

			new Swiper(slider, {
				slidesPerView: mobileLayout,
				spaceBetween: 12,
				speed: 600,
				navigation: {
					nextEl: slider.querySelector(
						".product-recommendations__slider-nav .swiper-button-next"
					),
					prevEl: slider.querySelector(
						".product-recommendations__slider-nav .swiper-button-prev"
					),
				},
				breakpoints: {
					576: {
						slidesPerView: 1.2,
						spaceBetween: 12,
					},
				},
			});
		});
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, {
							...fetchConfig(),
							...{ body },
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}
